import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { falCheck } from 'shared/@generated/icons/falCheck';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'shared/components/dropdown-menu';
import { Button } from 'shared/components/button';
import { FaIcon, Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { clsx } from 'clsx';

export function DropdownSelect<T>({
  title,
  options,
  value,
  onChange,
  triggerClassName,
  dense,
  bold,
  mutedDefaultValue,
  disabled,
  contentAlign = 'start',
  triggerIcon,
}: {
  title: string;
  options: { value: T | null; label: string; icon?: FaIcon }[];
  value: T | null;
  onChange: (value: T | null) => void;
  triggerClassName?: string;
  dense?: boolean;
  bold?: boolean;
  mutedDefaultValue?: boolean;
  disabled?: boolean;
  contentAlign?: 'start' | 'center' | 'end';
  triggerIcon?: FaIcon;
}) {
  const selectedIndex = options.findIndex((option) => option.value === value);
  const isDefaultValue = selectedIndex === -1 || value === null || value === undefined;
  const triggerButton = (
    <Button
      className={clsx(triggerClassName, {
        'border-brand-brown-dark/50': !isDefaultValue && dense,
      })}
      textClassName={clsx({
        'text-opacity-50': isDefaultValue && (!dense || mutedDefaultValue),
        '!font-normal': !bold,
        'text-lg': !dense,
      })}
      text={isDefaultValue ? title : options[selectedIndex]!.label}
      icon={disabled ? undefined : triggerIcon}
      iconPosition={triggerIcon ? 'left' : 'right'}
      color={'white'}
      dense={dense}
      disabled={disabled}
      onPress={() => {}}
    >
      {!disabled && (
        <Icon
          icon={falAngleDown}
          className={clsx(
            'text-brand-brown-dark',
            triggerIcon ? 'ml-2 size-3' : dense ? 'ml-1.5 size-4' : 'ml-3 size-4',
          )}
        />
      )}
    </Button>
  );

  if (disabled) {
    return triggerButton;
  }

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>

      <DropdownMenuContent
        style={{
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: 'min(300px, var(--radix-dropdown-menu-content-available-height))',
          overflowY: 'auto',
        }}
        align={contentAlign}
        collisionPadding={12}
      >
        {options.map((option, index) => (
          <DropdownMenuItem
            key={index.toString()}
            className={`!m-0 px-3 py-2 ${selectedIndex === index ? 'rounded bg-stone-100' : ''}`}
            onClick={() => onChange(option.value)}
          >
            {option.icon && <Icon icon={option.icon} className={'size-4'} />}

            <Text className={'flex-1'}>{option.label}</Text>

            {selectedIndex === index && (
              <Icon icon={falCheck} className={'text-brand-black/50 size-4'} />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
